'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type PortalProps = {
  /**
   * The children to render into the `document.body`.
   */
  children?: ReactNode;
};

/**
 * Portals provide a way to render children into a DOM node
 * that exists outside the DOM hierarchy of the parent component.
 *
 * The portal component renders its children into a new "subtree" outside of current DOM hierarchy.
 */
export const Portal = ({ children }: PortalProps) => {
  const [mountNode, setMountNode] = useState<Element | null>(null);

  // set the mount node after the component is mounted for hydration consistency,
  // because the document is available only after mounting
  useEffect(() => {
    setMountNode(document.body);
  }, []);

  return mountNode ? createPortal(children, mountNode) : null;
};
